/**
 * Где сейчас человек — из браузера.
 *
 * Спрашивается только по нажатию: чтобы задать вопрос про время в пути и
 * чтобы показать себя на карте. Нигде не сохраняется: ни у нас, ни в
 * браузере, и никуда не отправляется, кроме того самого вопроса про время
 * в пути. Браузер сам спросит разрешение, и отказ — нормальный ответ,
 * а не ошибка.
 */

/** Сколько ждать ответа от браузера. Дольше человек решит, что зависло. */
const TIMEOUT_MS = 10_000

export type Where = { latitude: number; longitude: number }

export async function whereAmI(): Promise<Where | null> {
  if (!('geolocation' in navigator)) return null
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
      // Отказ, выключенная геолокация, истёкшее ожидание — всё это «не знаю».
      () => resolve(null),
      { timeout: TIMEOUT_MS, maximumAge: 60_000, enableHighAccuracy: false },
    )
  })
}

/** Своя точка вместе с тем, насколько браузер в ней уверен. Метры. */
export type Fix = Where & { accuracy: number }

/**
 * Следить за своим положением, пока не остановят.
 *
 * Отдельно от `whereAmI` и не через него: разовый вопрос отвечает один раз
 * и молчит, а точка на карте, которая осталась там, где человек был десять
 * минут назад, врёт убедительнее, чем её отсутствие.
 *
 * Возвращает то, чем слежение прекращают. Прекращать обязательно: пока оно
 * живо, телефон держит приёмник включённым.
 */
export function watchMe(
  onFix: (fix: Fix) => void,
  onFail: () => void,
): () => void {
  if (!('geolocation' in navigator)) {
    onFail()
    return () => {}
  }
  const id = navigator.geolocation.watchPosition(
    (position) =>
      onFix({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        // Точность браузер знает всегда, но на всякий случай: без неё круг
        // выродится в точку и соврёт, что мы знаем место до метра.
        accuracy: Number.isFinite(position.coords.accuracy)
          ? position.coords.accuracy
          : 0,
      }),
    onFail,
    // Высокая точность здесь оправдана, в отличие от разового вопроса:
    // человек смотрит на свою точку и ждёт, что она стоит там, где он.
    { timeout: TIMEOUT_MS, maximumAge: 5_000, enableHighAccuracy: true },
  )
  return () => navigator.geolocation.clearWatch(id)
}
